import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import { SERVICES } from '../data/servicesData';
import { breadcrumbSchema, webPageSchema, serviceSchema } from '../lib/seo';
import { Button } from '../components/ui/button';
import { ServiceComparison } from '../components/ServiceComparison';

export default function ServicesPage() {
  const path = '/services';
  const title = 'Sales Function Design & Advisory Services | Founder Growth Collective';
  const description = 'Sales function design and advisory services for funded, founder-led startups: sales diagnostic, framework design, team & hiring, training & ramp, strategic advisory.';

  const schemas = [
    webPageSchema({ path, name: title, description }),
    breadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: 'Services', path: '/services' },
    ]),
    ...SERVICES.map(s => serviceSchema({ name: s.name, description: s.body, path: `/services/${s.slug}` })),
  ];

  return (
    <div className="bg-[#EBE4DE] text-[#2E2C28] min-h-screen">
      <SEO title={title} description={description} path={path} schemas={schemas} />
      <Navbar />
      <main className="pt-36 pb-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <Breadcrumbs items={[{ name: 'Home', path: '/' }, { name: 'Services', path: '/services' }]} />

          <header className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-4">
              <p className="sec-num text-sm tracking-[0.3em] uppercase">/ Services</p>
            </div>
            <div className="lg:col-span-8">
              <h1 className="font-serif text-[40px] sm:text-[52px] lg:text-[68px] leading-[1.02] tracking-[-0.015em] text-[#2E2C28] font-light">
                Custom sales infrastructure built for <span className="italic text-[#B5876C]">funded startups</span>.
              </h1>
              <p className="mt-8 text-lg text-[#4a463f] font-light leading-relaxed max-w-2xl">
                Transitioning from individual founder efforts to scalable revenue cycles.
              </p>
            </div>
          </header>

          <section aria-label="Core services" className="mt-20 divide-y divide-[#d9d2ca] border-t border-b border-[#d9d2ca]">
            {SERVICES.map((s, i) => (
              <article key={s.slug} className="grid grid-cols-1 lg:grid-cols-12 gap-6 py-10 lg:py-14">
                <div className="lg:col-span-1 sec-num text-xl">0{i + 1}</div>
                <div className="lg:col-span-4">
                  <h2 className="font-serif text-2xl lg:text-3xl text-[#2E2C28] font-light leading-tight">{s.name}</h2>
                </div>
                <div className="lg:col-span-6">
                  <p className="text-base lg:text-lg text-[#4a463f] font-light leading-relaxed">{s.body}</p>
                </div>
                <div className="lg:col-span-1 lg:justify-self-end">
                  <Link to={`/services/${s.slug}`} aria-label={`Learn more about ${s.name}`} className="inline-flex items-center justify-center w-11 h-11 border border-[#B5876C]/40 hover:bg-[#B5876C] hover:text-[#EBE4DE] transition-colors">
                    <ArrowUpRight size={16} strokeWidth={1.5} />
                  </Link>
                </div>
              </article>
            ))}
          </section>

          <section className="mt-20 lg:mt-28"><ServiceComparison current="infrastructure" /></section>

          <div className="mt-16">
            <Button asChild className="bg-[#2E2C28] hover:bg-[#B5876C] text-[#EBE4DE] rounded-none h-14 px-8 text-[12px] tracking-[0.2em] uppercase">
              <Link to="/contact" className="inline-flex items-center gap-3">Book a Discovery Call <ArrowUpRight size={16} strokeWidth={1.5} /></Link>
            </Button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
